import { useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  alpha,
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  Typography,
} from '@mui/material';
import {
  Close,
  PhoneInTalk as PhoneInTalkIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { formatPhone } from '../utils/phone';
import CallDetailPanel from './CallDetailPanel';
import CallStatusChip from './CallStatusChip';
import { useCallDetailObserver, type CallDetailObserverEvent } from '../hooks/useCallDetailObserver';
import { mergeCallDetailState } from './call-detail/detailState';
import type { CallHistoryItem } from '../types';

interface CallDetailDrawerProps {
  open: boolean;
  callId: string | null;
  initialCall?: CallHistoryItem | null;
  initialTab?: 'information' | 'general' | 'variables' | 'dataCollected' | 'scripts' | 'diagnostics';
  controlMode?: 'default' | 'incoming' | 'outbound_self';
  onClose: () => void;
  canViewCostCurrency: boolean;
  canViewCostCredits: boolean;
  contactVariables?: Record<string, string>;
}

export default function CallDetailDrawer({
  open,
  callId,
  initialCall = null,
  initialTab = 'information',
  controlMode = 'default',
  onClose,
  canViewCostCurrency,
  canViewCostCredits,
  contactVariables,
}: CallDetailDrawerProps) {
  const { t } = useTranslation(['components', 'pages', 'common']);
  const theme = useTheme();
  const [callData, setCallData] = useState<CallHistoryItem | null>(initialCall);
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);

  const fetchCallDetail = useCallback(async (id: string) => {
    requestIdRef.current += 1;
    const currentRequestId = requestIdRef.current;
    setLoading(true);
    try {
      const response = await api.get<CallHistoryItem>(`/api/calls/${id}/`);
      if (currentRequestId === requestIdRef.current) {
        setCallData((prevCall) => mergeCallDetailState(prevCall, response.data));
      }
    } catch (err) {
      console.error('Failed to load call detail:', err);
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    setCallData((prevCall) => {
      if (initialCall?.id === callId) {
        return initialCall;
      }
      if (prevCall?.id === callId) {
        return prevCall;
      }
      return null;
    });
  }, [callId, initialCall]);

  useEffect(() => {
    if (!open || !callId) return;
    fetchCallDetail(callId);
  }, [callId, fetchCallDetail, open]);

  const handleCallObserverEvent = useCallback(
    (event: CallDetailObserverEvent) => {
      if (!event.id || event.id !== callId) {
        return;
      }

      setCallData((prevCall) => (prevCall ? mergeCallDetailState(prevCall, event) : prevCall));
      fetchCallDetail(event.id);
    },
    [callId, fetchCallDetail],
  );

  useCallDetailObserver(callData?.id, {
    enabled: open && Boolean(callData?.id),
    onEvent: handleCallObserverEvent,
  });

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100vw', md: '60vw' },
          maxWidth: { xs: '100vw', md: '60vw' },
          minWidth: { xs: '100vw', md: 360 },
          height: '100%',
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: alpha(theme.palette.primary.main, 0.08),
          }}
        >
          <Box sx={{ minWidth: 0, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <PhoneInTalkIcon color="primary" />
              <Typography variant="h6" sx={{ whiteSpace: 'nowrap' }}>
                {t('components:callDetails.title')}
              </Typography>

              {callData && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2, width: '100%', justifyContent: 'space-between', textAlign: 'left' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {callData.originLineName
                      ? `${callData.originLineName} • ${formatPhone(callData.toNumber)}`
                      : formatPhone(callData.toNumber)}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                    <CallStatusChip
                      status={callData.status}
                      isConverted={callData.isConverted}
                      isOptout={callData.isOptout}
                      isMismatch={callData.isMismatch}
                    />
                  </Box>
                </Box>
              )}
            </Box>

          </Box>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, overflowX: 'hidden', overflowY: 'auto', p: { xs: 2, md: 3 } }}>
          {callData ? (
            <CallDetailPanel
              call={callData}
              initialTab={initialTab}
              controlMode={controlMode}
              canViewCostCurrency={canViewCostCurrency}
              canViewCostCredits={canViewCostCredits}
              contactVariables={contactVariables}
            />
          ) : (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {loading ? (
                <CircularProgress />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('components:callDetails.tabs.empty')}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
